<template>
  <v-dialog v-model="dialog" persistent width="1024">
    <v-card>
      <v-card-title>
        Aprobarea cererii de modificare note
        <v-spacer></v-spacer>
        <v-btn icon dark color="black" @click="dialog = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <v-card-text>
        <v-form>
          <v-container>
            <h3 class="custom-heading">Datele despre solicitant</h3>
            <v-row>
              <v-col cols="12" sm="4">
                <v-text-field v-model="user" label="Profesorul solicitant" readonly></v-text-field>
              </v-col>
              <v-col cols="12" sm="4">
                <v-text-field v-model="facultatea" label="Facultatea" readonly></v-text-field>
              </v-col>
              <v-col cols="12" sm="4">
                <v-text-field v-model="grupa" label="Grupa" readonly></v-text-field>
              </v-col>

              <v-col cols="12" sm="4">
                <v-text-field v-model="disciplina" label="Disciplina" readonly></v-text-field>
              </v-col>
              <v-col cols="12" sm="4">
                <v-text-field v-model="cause" label="Tipul cererii" readonly></v-text-field>
              </v-col>
              <v-col cols="12" sm="4">
                <v-text-field v-model="date" label="Data emiterii" readonly></v-text-field>
              </v-col>

              <v-col cols="12">
                <v-textarea v-model="explicatia" label="Explicația cererii" readonly auto-grow></v-textarea>
              </v-col>

              <v-col cols="12">
                <h3 class="custom-heading">Modificări de note solicitate</h3>
                <v-data-table :headers="gradeHeaders" :items="gradeChanges" hide-default-footer class="elevation-1">
                  <template v-slot:item.old_value="{ item }">
                    <span :class="{ 'red--text': item.new_value !== item.old_value }">{{ item.old_value }}</span>
                  </template>

                  <template v-slot:item.new_value="{ item }">
                    <span :class="{ 'green--text': item.new_value !== item.old_value }">{{ item.new_value }}</span>
                  </template>
                </v-data-table>
              </v-col>

              <v-col cols="12">
                <h3 class="custom-heading">Istoricul comentariilor</h3>
                <v-card outlined class="mt-2 mb-4">
                  <v-card-text>
                    <div v-if="!commentsHistory || commentsHistory.length === 0" class="text-subtitle-1">
                      Nu există comentarii anterioare
                    </div>
                    <template v-else>
                      <div v-for="(item, index) in commentsHistory" :key="index" class="mb-3">
                        <div class="d-flex align-center">
                          <strong>{{ item.user }}</strong>
                          <span class="ml-2 grey--text text--darken-1">({{ formatDate(item.date) }})</span>
                          <v-chip x-small class="ml-2" :color="getStatusColor(item.status)">
                            {{ item.status }}
                          </v-chip>
                        </div>
                        <div class="ml-3 mt-1" v-if="item.comment">{{ item.comment }}</div>
                        <v-divider v-if="index < commentsHistory.length - 1" class="my-2"></v-divider>
                      </div>
                    </template>
                  </v-card-text>
                </v-card>
              </v-col>

              <v-col cols="12">
                <v-textarea v-model="comment" label="Adăugați comentariul dvs." persistent-hint
                  hint="Comentariul va fi salvat împreună cu acțiunea dvs."></v-textarea>
              </v-col>
            </v-row>
          </v-container>

          <!-- Action buttons vary based on role and request status -->
          <v-card-actions>
            <v-row justify="end">
              <!-- Reject button shown for deans, rectors, and admins -->
              <v-btn v-if="canReject" style="color: white; background-color: #e53935" variant="text"
                @click="showConfirmCard('reject')" class="mr-2 mt-2">
                Respingere
              </v-btn>

              <!-- Dean or Rector approve button -->
              <v-btn v-if="canApprove && (isDean || isRector || isAdmin)"
                style="color: white; background-color: #2d8654" variant="text" @click="showConfirmCard('approve')"
                class="mr-2 mt-2">
                Aprobare
              </v-btn>

              <!-- Operator buttons: Mark as "În proces" or "Îndeplinit" -->
              <v-btn v-if="isOperator && request && request.status_id === ResponseType.RECTOR_APPROVED"
                style="color: white; background-color: #ff9800" variant="text" @click="showConfirmCard('markInProcess')"
                class="mr-2 mt-2">
                Marcare în Proces
              </v-btn>

              <v-btn
                v-if="isOperator && request && (request.status_id === ResponseType.RECTOR_APPROVED || request.status_id === ResponseType.IN_PROCESS)"
                style="color: white; background-color: #4caf50" variant="text" @click="showConfirmCard('markComplete')"
                class="mr-2 mt-2">
                Marcare ca Îndeplinit
              </v-btn>
            </v-row>
          </v-card-actions>
        </v-form>
      </v-card-text>
      <ConfirmCard v-if="confirmCard" :title="confirmTitle" @cancel="cancelConfirmCard" @confirm="confirmAction" />
    </v-card>
  </v-dialog>
</template>

<script>
import * as api from "../request/api";
import * as helper from "@/helper/helper.js";
import ConfirmCard from "@/components/elements/ConfirmCard.vue";
import { mapGetters } from "vuex";

const ResponseType = {
  TEACHER_CREATED: 13,       // Emis de profesor
  DEAN_APPROVED: 14,         // Aprobat de Decan
  RECTOR_APPROVED: 3,       // Aprobat de Rector
  COMPLETED: 9,             // Îndeplinit
  REJECTED: 4,              // Respins
  IN_PROCESS: 8             // În proces lucrare
};

export default {
  components: {
    ConfirmCard,
  },
  data() {
    return {
      dialog: false,
      request: null,
      user: '',
      facultatea: '',
      grupa: '',
      disciplina: '',
      explicatia: '',
      cause: '',
      date: '',
      gradeChanges: [],
      comment: '',
      commentsHistory: [],
      confirmCard: false,
      confirmTitle: "",
      pendingAction: null,
      gradeHeaders: [
        { text: 'Student', value: 'student_name', width: '25%' },
        { text: 'Disciplina', value: 'disciplina_name', width: '25%' },
        { text: 'Tip evaluare', value: 'grade_type', width: '20%' },
        { text: 'Valoare veche', value: 'old_value', width: '15%' },
        { text: 'Valoare nouă', value: 'new_value', width: '15%' }
      ],
      ResponseType
    };
  },
  computed: {
    ...mapGetters(["getUser"]),
    isAdmin() {
      return this.getUser.role === "admin";
    },
    isDean() {
      return this.getUser.role === "usersef";
    },
    isRector() {
      return this.getUser.role === "rector";
    },
    isOperator() {
      return this.getUser.role === "operator";
    },
    isTeacher() {
      return this.getUser.role === "profesor";
    },

    // Check if this is a self approval (dean approving own request)
    isSelfApproval() {
      return this.isDean &&
        this.request &&
        this.request.userId === this.getUser.userId;
    },

    // Determine if the current user can approve this specific request
    canApprove() {
      if (!this.request) return false;

      // Admin can approve any request
      if (this.isAdmin) {
        return true;
      }

      // Dean can approve teacher requests (status 13)
      if (this.isDean && this.request.status_id === ResponseType.TEACHER_CREATED) {
        return true;
      }

      // Rector can approve dean-approved requests (status 14)
      if (this.isRector && this.request.status_id === ResponseType.DEAN_APPROVED) {
        return true;
      }

      return false;
    },

    // Determine if the current user can reject this specific request
    canReject() {
      if (!this.request) return false;

      // Admin can reject any request that isn't already completed or rejected
      if (this.isAdmin &&
        this.request.status_id !== ResponseType.COMPLETED &&
        this.request.status_id !== ResponseType.REJECTED) {
        return true;
      }

      // Dean can reject teacher requests
      if (this.isDean && this.request.status_id === ResponseType.TEACHER_CREATED) {
        return true;
      }

      // Rector can reject dean-approved requests
      if (this.isRector && this.request.status_id === ResponseType.DEAN_APPROVED) {
        return true;
      }

      return false;
    }
  },
  methods: {
    open(item) {
      if (!item) return;

      this.request = item;
      this.dialog = true;

      this.loadRequestDetails(item.requestId);
    },

    async loadRequestDetails(requestId) {
      try {
        // Load full request details
        const requestDetails = await api.getTeacherRequestById(requestId);
        this.populateFormData(requestDetails);

        // Load comments history
        this.commentsHistory = await api.getTeacherRequestCommentsHistory(requestId);
      } catch (error) {
        console.error("Error loading request details:", error);
        this.$emit("error", "Nu s-au putut încărca detaliile cererii");
      }
    },

    populateFormData(data) {
      this.user = data.user || '';
      this.facultatea = data.facultatea || '';
      this.grupa = data.grupa || '';
      this.disciplina = data.disciplina || '';
      this.cause = data.cause || '';
      this.date = this.formatCreatedAt(data.created_at || data.date);
      this.explicatia = data.explicatia || '';
      this.gradeChanges = data.gradeChanges || [];
    },

    formatCreatedAt(dateString) {
      if (!dateString) return '';
      // Handle both date formats that might come from the server
      try {
        return new Date(dateString).toLocaleString('ro-RO', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit'
        });
      } catch (e) {
        console.error("Error formatting date:", e);
        return dateString; // Return the original string if parsing fails
      }
    },

    formatDate(dateString) {
      if (!dateString) return '';
      return new Date(dateString).toLocaleString('ro-RO', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    },

    getStatusColor(status) {
      const statusColors = {
        'Respins': 'error',
        'Emis de profesor': 'primary',
        'Aprobat de Decan': 'info',
        'Aprobat de Rector': 'purple',
        'Îndeplinit': 'success',
        'În proces lucrare': 'orange',
        'În proces': 'warning'
      };

      if (!status) return 'grey';

      for (const [key, value] of Object.entries(statusColors)) {
        if (status.includes(key)) {
          return value;
        }
      }

      return 'grey';
    },

    showConfirmCard(action) {
      this.confirmCard = true;
      this.pendingAction = action;

      switch (action) {
        case 'approve':
          this.confirmTitle = "Doriți să aprobați această cerere?";
          break;
        case 'reject':
          this.confirmTitle = "Doriți să respingeți această cerere?";
          break;
        case 'markInProcess':
          this.confirmTitle = "Doriți să marcați cererea ca fiind în proces de lucru?";
          break;
        case 'markComplete':
          this.confirmTitle = "Doriți să marcați cererea ca îndeplinită?";
          break;
        default:
          this.confirmTitle = "Confirmați acțiunea?";
      }
    },

    cancelConfirmCard() {
      this.confirmCard = false;
      this.pendingAction = null;
    },

    confirmAction() {
      this.confirmCard = false;

      switch (this.pendingAction) {
        case 'approve':
          this.approveRequest();
          break;
        case 'reject':
          this.rejectRequest();
          break;
        case 'markInProcess':
          this.markInProcess();
          break;
        case 'markComplete':
          this.markComplete();
          break;
      }

      this.pendingAction = null;
    },

    async approveRequest() {
      let responseTypeId;

      // Determine next status based on current status and role
      if (this.isDean && this.request.status_id === ResponseType.TEACHER_CREATED) {
        responseTypeId = ResponseType.DEAN_APPROVED;
      } else if (this.isRector && this.request.status_id === ResponseType.DEAN_APPROVED) {
        responseTypeId = ResponseType.RECTOR_APPROVED;
      } else if (this.isAdmin) {
        // Admin can push to next stage based on current status
        if (this.request.status_id === ResponseType.TEACHER_CREATED) {
          responseTypeId = ResponseType.DEAN_APPROVED;
        } else if (this.request.status_id === ResponseType.DEAN_APPROVED) {
          responseTypeId = ResponseType.RECTOR_APPROVED;
        } else if (this.request.status_id === ResponseType.RECTOR_APPROVED) {
          responseTypeId = ResponseType.COMPLETED;
        } else {
          // Default case - shouldn't normally happen
          responseTypeId = ResponseType.COMPLETED;
        }
      } else {
        this.$emit("error", "Nu aveți permisiunea de a aproba această cerere");
        return;
      }

      await this.submitResponse(responseTypeId);
    },

    async rejectRequest() {
      // Require comments for rejection
      if (!this.comment || this.comment.trim() === '') {
        this.$emit("error", "Adăugarea unui comentariu este obligatorie pentru respingerea cererii");
        return;
      }
      
      await this.submitResponse(ResponseType.REJECTED);
    },

    async markInProcess() {
      await this.submitResponse(ResponseType.IN_PROCESS);
    },

    async markComplete() {
      await this.submitResponse(ResponseType.COMPLETED);
    },

    async submitResponse(responseTypeId) {
      if (!this.request || !this.request.requestId) {
        this.$emit("error", "Date insuficiente pentru a procesa cererea");
        return;
      }

      try {
        // Change the property name from request_id to request_teacher_id
        const responseData = {
          request_teacher_id: this.request.requestId,
          response_type_id: responseTypeId,
          comments: this.comment || ''
        };

        // Submit the response
        await api.saveTeacherResponse(responseData);

        this.dialog = false;

        // Set success message based on action
        let successMessage = '';
        switch (responseTypeId) {
          case ResponseType.REJECTED:
            successMessage = "Cererea a fost respinsă";
            break;
          case ResponseType.DEAN_APPROVED:
            successMessage = "Cererea a fost aprobată de către decan";
            break;
          case ResponseType.RECTOR_APPROVED:
            successMessage = "Cererea a fost aprobată de către rector";
            break;
          case ResponseType.IN_PROCESS:
            successMessage = "Cererea a fost marcată ca fiind în proces de lucru";
            break;
          case ResponseType.COMPLETED:
            successMessage = "Cererea a fost marcată ca îndeplinită";
            break;
          default:
            successMessage = "Acțiunea a fost înregistrată cu succes";
        }

        this.$emit("success", successMessage);

        // Refresh data without changing the URL
        this.$emit("update-data");

      } catch (error) {
        console.error("Error processing request:", error);
        // Log the error response if available
        if (error.response) {
          console.error("Server response:", error.response.data);
        }
        this.$emit("error", "A apărut o eroare la procesarea cererii");
      }
    }
  }
};
</script>

<style scoped>
.custom-heading {
  margin-bottom: 16px;
  color: #424242;
  text-align: left;
}
</style>
